import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { BookingModal } from '@/components/booking/BookingModal'
import { Gallery } from '@/components/gallery'

interface CreativeTemplateProps {
    profile: PublicProfile
}

export function CreativeTemplate({ profile }: CreativeTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Full-width Hero Image */}
            {profile.cover_image ? (
                <div className="relative h-64 md:h-80 lg:h-96">
                    <img
                        src={profile.cover_image}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(to bottom, transparent 40%, ${colors.colors.background} 100%)`,
                        }}
                    />
                </div>
            ) : (
                <div
                    className="h-32 md:h-40"
                    style={{ backgroundColor: colors.colors.primary }}
                />
            )}

            {/* Profile Header with overlapping avatar */}
            <div className="relative px-4 pb-6 md:px-6 md:pb-8 lg:px-8 -mt-16 md:-mt-20 relative z-10">
                <div className="flex items-end gap-4 md:gap-6">
                    {profile.profile_image ? (
                        <img
                            src={profile.profile_image}
                            alt={profile.business_name}
                            className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 object-cover shadow-xl"
                            style={{ borderColor: colors.colors.accent }}
                        />
                    ) : (
                        <div
                            className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 shadow-xl flex items-center justify-center"
                            style={{ borderColor: colors.colors.accent, backgroundColor: colors.colors.secondary }}
                        >
                            <span
                                className="text-3xl md:text-4xl font-bold"
                                style={{ color: colors.colors.primary }}
                            >
                                {profile.business_name[0]}
                            </span>
                        </div>
                    )}
                    <div className="pb-2">
                        <h1
                            className="text-2xl md:text-3xl lg:text-4xl font-bold"
                            style={{ fontFamily: typography.heading }}
                        >
                            {profile.business_name}
                        </h1>
                        {profile.tagline && (
                            <p className="text-sm md:text-base mt-1" style={{ color: colors.colors.textMuted }}>
                                {profile.tagline}
                            </p>
                        )}
                        {profile.location && (
                            <p className="text-xs md:text-sm mt-1" style={{ color: colors.colors.textMuted }}>
                                📍 {profile.location}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Bio - Editorial Style */}
            {profile.bio && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-4xl lg:mx-auto">
                    <p
                        className="text-base md:text-lg leading-relaxed"
                        style={{ lineHeight: 1.8 }}
                    >
                        {profile.bio}
                    </p>
                </section>
            )}

            {/* Large Gallery Focus - Creative Showcase */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8">
                    <h2
                        className="text-xl md:text-2xl font-bold mb-6"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.creative.portfolio')}
                    </h2>
                    <Gallery
                        images={profile.gallery}
                        layout="featured"
                        limit={6}
                        enableLightbox
                        showCaptions
                        aspectRatio="square"
                        gapSize={4}
                    />
                </section>
            )}

            {/* Services - Minimal Style */}
            {profile.services && profile.services.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-4xl lg:mx-auto border-t" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-6"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.creative.services')}
                    </h2>
                    <div className="space-y-4">
                        {profile.services.map((service) => (
                            <div
                                key={service.id}
                                className="flex flex-col md:flex-row md:justify-between md:items-center py-4 border-b"
                                style={{ borderColor: colors.colors.secondary }}
                            >
                                <div className="flex-1">
                                    <h3
                                        className="font-bold text-lg"
                                        style={{ color: colors.colors.text }}
                                    >
                                        {service.name}
                                    </h3>
                                    {service.description && (
                                        <p
                                            className="text-sm mt-1"
                                            style={{ color: colors.colors.textMuted }}
                                        >
                                            {service.description}
                                        </p>
                                    )}
                                </div>
                                <div className="mt-2 md:mt-0 md:ml-4 flex items-center gap-4">
                                    {service.price_display !== 'hidden' && service.price && (
                                        <span
                                            className="text-xl font-bold"
                                            style={{ color: colors.colors.accent }}
                                        >
                                            {service.price_display === 'from' && 'From '}${service.price.toFixed(2)}
                                        </span>
                                    )}
                                    {service.duration_minutes && (
                                        <span
                                            className="text-xs"
                                            style={{ color: colors.colors.textMuted }}
                                        >
                                            {service.duration_minutes} min
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Social Links */}
            {profile.social_links && profile.social_links.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:max-w-4xl lg:mx-auto border-t" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-4"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.creative.connect')}
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        {profile.social_links.map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-opacity hover:opacity-80"
                                style={{
                                    backgroundColor: colors.colors.secondary,
                                    color: colors.colors.primary,
                                }}
                            >
                                {link.label || link.platform}
                            </a>
                        ))}
                    </div>
                </section>
            )}

            {/* Contact CTA */}
            <section
                className="px-4 py-12 md:py-16 lg:px-8 text-center"
                style={{ backgroundColor: colors.colors.primary }}
            >
                <h2
                    className="text-2xl md:text-3xl font-bold mb-4"
                    style={{ fontFamily: typography.heading, color: colors.colors.secondary }}
                >
                    {t('template.creative.letsCreateTogether')}
                </h2>
                <p className="mb-6 opacity-90" style={{ color: colors.colors.secondary }}>
                    {t('template.creative.readyToCollaborate')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
                    {profile.phone && (
                        <a
                            href={`tel:${profile.phone}`}
                            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold transition-opacity hover:opacity-90"
                            style={{
                                backgroundColor: colors.colors.accent,
                                color: colors.colors.primary,
                            }}
                        >
                            📞 Call {profile.phone}
                        </a>
                    )}
                    {profile.email && (
                        <a
                            href={`mailto:${profile.email}`}
                            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold transition-opacity hover:opacity-90"
                            style={{
                                backgroundColor: colors.colors.secondary,
                                color: colors.colors.primary,
                            }}
                        >
                            ✉️ {profile.email}
                        </a>
                    )}
                    {profile.services && profile.services.length > 0 && (
                        <button
                            onClick={() => setIsBookingOpen(true)}
                            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold transition-opacity hover:opacity-90"
                            style={{
                                backgroundColor: colors.colors.primary,
                                color: colors.colors.secondary,
                            }}
                        >
                            📅 Book Now
                        </button>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer
                className="px-4 py-6 md:px-6 md:py-8 lg:px-8 text-center"
                style={{ backgroundColor: colors.colors.background }}
            >
                <a
                    href="/"
                    className="text-sm"
                    style={{ color: colors.colors.textMuted, textDecoration: 'none' }}
                >
                    {t('template.creative.poweredBy')}
                </a>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default CreativeTemplate;
